/**
 * Utilitaires pour manipuler les tokens JWT
 */

/**
 * Décode un token JWT et retourne son payload
 * @param token - Token JWT à décoder
 * @returns Payload décodé ou null si invalide
 */
export function decodeJwtPayload(token: string): any | null {
  try {
    // Un JWT est composé de 3 parties séparées par des points: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Le payload est la deuxième partie (index 1)
    const payload = parts[1];

    // Décoder le base64url
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

    // Parser le JSON
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Erreur lors du décodage du token JWT:', error);
    return null;
  }
}

/**
 * Vérifie si un token JWT est expiré
 * @param token - Token JWT à vérifier
 * @returns true si le token est expiré, false sinon
 */
export function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // exp est en secondes, Date.now() est en millisecondes
  const expirationTime = payload.exp * 1000;
  return Date.now() >= expirationTime;
}

/**
 * Extrait l'ID utilisateur (sub) du token JWT
 * @param token - Token JWT
 * @returns ID utilisateur ou null si invalide
 */
export function getUserIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  return payload?.sub || payload?.id || null;
}

/**
 * Extrait les données utilisateur complètes du token JWT
 * @param token - Token JWT
 * @returns Objet user complet ou null si invalide
 */
export function getUserDataFromToken(token: string): any | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  // DEBUG: Afficher le payload complet
  console.log('[JWT Utils] Token payload:', payload);

  // Exclure les champs techniques du JWT
  const { iat, exp, ...userData } = payload;

  // Si les données LaBase sont dans app_data.labase, les extraire au niveau racine
  if (userData.app_data?.labase) {
    console.log('[JWT Utils] Extracting app_data.labase:', userData.app_data.labase);
    return {
      ...userData,
      ...userData.app_data.labase
    };
  }

  return userData;
}